import { inject, injectable } from 'inversify';
import React from 'react';

import { useAxios, useFetch, UseFetchArgs } from '@project44/ui-utils';
import { DtoBase, TenantModels } from '@project44-models/iam-gateway';

import { AxiosDelegationStrategyEnum } from '../../../../shared/core/domain/enums';
import { ParametersValueObject } from '../../../../shared/core/domain/ParameterValueObject';
import { ModifiedAxiosRequestConfigAdapter } from '../../../../shared/core/infrastructrue/adapters/ModifiedAxiosRequestConfig.adapter';
import { TUseSwitchTenantHook, TUseTenantQueryHook } from '../../domain';
import { TenantServiceClientAdapter } from '../adapters/TenantServiceClientAdapter';

@injectable()
export class TenantHookFactory {
  constructor(
    @inject(TenantServiceClientAdapter)
    private readonly tenantRepository: TenantServiceClientAdapter,

    @inject(ModifiedAxiosRequestConfigAdapter)
    private readonly modifiedAxiosRequestConfigAdapter: ModifiedAxiosRequestConfigAdapter,
  ) {}

  public composeUseTenantQueryHook(): TUseTenantQueryHook {
    const { REACT_HOOK_DELEGATION } = AxiosDelegationStrategyEnum;

    return ({ tenantQueryDto }) => {
      const [useFetchHookConfig, setUseFetchHookConfig] = React.useState<
        | undefined
        | UseFetchArgs<DtoBase<TenantModels.TenantQueryResultsViewModel>>
      >(undefined);

      React.useEffect((): void => {
        if (!!tenantQueryDto) {
          this.tenantRepository
            .query<AxiosDelegationStrategyEnum.REACT_HOOK_DELEGATION>(
              ParametersValueObject.create({
                tenantQueryDto: tenantQueryDto,
                axiosDelegationStrategy: REACT_HOOK_DELEGATION,
              }),
            )
            .then(({ result }) => {
              setUseFetchHookConfig(
                this.modifiedAxiosRequestConfigAdapter.toUseFetchArgs({
                  modifiedAxiosRequestConfig: result,
                }),
              );
            });
        }
      }, [tenantQueryDto]);

      return useFetch<DtoBase<TenantModels.TenantQueryResultsViewModel>>(
        useFetchHookConfig === undefined
          ? this.modifiedAxiosRequestConfigAdapter.toNullableUseFetchArgs()
          : useFetchHookConfig,
      );
    };
  }

  public composeUseSwitchTenantHook(): TUseSwitchTenantHook {
    const { REACT_HOOK_DELEGATION } = AxiosDelegationStrategyEnum;

    return () => {
      const [axiosDataState, axiosRequestFn] = useAxios();

      const switchTenant = async (options: {
        tenantDto: DtoBase<TenantModels.TenantViewModel>;
      }): Promise<void> => {
        const { tenantDto } = options;

        const { result } =
          await this.tenantRepository.switchTenant<AxiosDelegationStrategyEnum.REACT_HOOK_DELEGATION>(
            {
              tenantDto,
              axiosDelegationStrategy: REACT_HOOK_DELEGATION,
            },
          );

        await axiosRequestFn(
          this.modifiedAxiosRequestConfigAdapter.toUseAxiosConfig({
            modifiedAxiosRequestConfig: result,
          }),
        );
      };

      return [axiosDataState, switchTenant];
    };
  }
}
